import axios from 'axios';
import { apiUrls } from '@/constant/configure';
import sessionStore from '@/store/modules/sessionStore';
import { getAnonymousToken, refreshToken, removeAnonymousToken, signOutSession } from '@/services/auth.service';
import checkExpireTime from '@/utils/checkExpireTime';
import { isEmbedded, requestEmbedToken } from '@/utils/embed';

const token = sessionStore.getState()?.accessToken;
// console.log('%c token for init', 'color:red;', token);

axios.defaults.withCredentials = true;
const api = axios.create({
    baseURL: apiUrls.manager,
});

api.defaults.headers.common['Authorization'] = !!token ? `Bearer ${token}` : undefined;

async function getUserToken() {
    try {
        const { accessToken, expiresIn } = sessionStore.getState();
        const isTokenLive = !!accessToken ? checkExpireTime(expiresIn) : false;

        if (isTokenLive) {
            return {
                accessToken: accessToken,
                expiresIn: expiresIn,
            };
        } else {
            return {
                accessToken: '',
                expiresIn: '',
            };
        }
    } catch (e) {
        return {
            accessToken: '',
            expiresIn: '',
        };
    }
}

// getUserToken().catch(r => r)

async function setApiHeader(token?: string) {
    const { accessToken } = await getUserToken();
    token = token || accessToken;

    api.defaults.headers.common['Authorization'] = !!token ? `Bearer ${token}` : `Bearer ${await getAnonymousToken()}`;
}

api.interceptors.request.use(
    async (config) => {
        // const source = axios.CancelToken.source()
        // config.cancelToken = source.token;
        // setTimeout(() => {
        //     source.cancel('over 2000ms time out')
        // }, 2000)
        // const {accessToken} = await getUserToken();
        //
        // if (!config?.headers?.Authorization) {
        //     // console.log(await getDefaultToken());
        //     config.headers.Authorization = !!accessToken ? `Bearer ${accessToken}` : `Bearer ${await getAnonymousToken()}`;
        //
        //     console.log('%c excepted-token', 'color:purple;', config?.headers?.Authorization)
        // }

        return config;
    },
    async (error) => {
        await Promise.reject(error);
    },
);

/** 요청 하나당 토큰 갱신 재시도 횟수 상한. 요청마다 따로 센다 (앱 전체가 하나를 공유하면 동시 요청이 서로 횟수를 깎는다) */
const MAX_TOKEN_RETRY = 2;

/**
 * 세션이 끝났음을 화면에 알린다.
 * 여기서 라우터나 토스트를 직접 부르면 라우터 → 서비스 → 이 파일 순환 import 가 생기므로
 * 창 이벤트로만 던지고, App 에서 받아 안내와 이동을 한다.
 */
function announceSessionExpired(reason: 'expired' | 'refresh-denied') {
    window.dispatchEvent(new CustomEvent('flowing:session-expired', { detail: { reason } }));
}

api.interceptors.response.use(
    async function (res) {
        if (res.status === 205) {
            // access_token 기간 만료 시,
            removeAnonymousToken();

            const { updateToken, clearSession } = sessionStore.getState();
            try {
                // 글로벌 소듀 안에 끼워 넣은 창은 갱신 쿠키를 못 쓸 수 있어 부모 창에서 새 토큰을 받는다 (src/utils/embed.ts)
                const renew = isEmbedded() ? requestEmbedToken : refreshToken;
                const accessToken = await renew()
                    .then(async (r) => {
                        const { accessToken, expiresIn } = r;
                        updateToken(accessToken, expiresIn);

                        return r.accessToken;
                    })
                    .catch(async (e) => {
                        if (e.status === 403 || e.status === 500) {
                            clearSession();
                            return await getAnonymousToken();
                        } else {
                            throw e;
                        }
                    });
                const config = res.config as typeof res.config & { __tokenRetry?: number };

                await setApiHeader(!!accessToken ? accessToken : undefined);

                const retried = (config.__tokenRetry ?? 0) + 1;

                if (retried <= MAX_TOKEN_RETRY) {
                    config.__tokenRetry = retried;
                    config.headers.Authorization = `Bearer ${accessToken}`;
                    return await api(config);
                }

                // 재시도를 다 썼다. 205 를 성공처럼 돌려주면 호출한 쪽이 빈 응답을 정상으로 믿는다.
                announceSessionExpired('expired');
                return Promise.reject(Object.assign(new Error('session expired'), { status: 205, response: res }));
            } catch (e: any) {
                // 갱신 자체가 실패했다. 안내와 로그인 이동은 App 이 맡는다.
                announceSessionExpired('expired');
                throw e;
            }
        }

        return res;
    },
    async function (err) {
        const { conf: conf, response } = err;
        // if (axios.isCancel(err)) {
        //     return Promise.reject(new Error('Request timed out'));
        // }
        if (response.status === 403) {
            const reqUrl = response.config?.url;

            if (reqUrl === '/auth/web/refreshToken') {
                await signOutSession();
                announceSessionExpired('refresh-denied');
            }
        }

        return Promise.reject(response || err);
    },
);

export default api;
